import React, { ReactNode } from 'react'
import { Col, Dropdown, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'

import * as Paging from '../pagination/pagination'
import * as Sort from './SortFilter'
import { ChevronDown, ChevronRight, Minus, RefreshCw } from 'react-feather'

import './Listing.css'
import { Loading } from '../Loading/Loading'
import itemRender from './ItemRender'

interface FilterDropdownOption {
	value: string
	text: string
}

interface SectionStyles {
	thead?: React.CSSProperties
	trDepth0?: React.CSSProperties
	trDepth1?: React.CSSProperties
	trDepth2?: React.CSSProperties
}

interface ListingAction {
	name: string
	method: (selected: string[]) => Promise<void>
}

interface ListingColumn<T> {
	// body stuff
	value: (item: T) => string
	render: (item: T) => JSX.Element
	colSpan?: (item: T) => number
	hidden?: (item: T) => boolean
	customSortCompare?: (a: string, b: string) => number
	// header stuff
	showHeader: boolean
	headerText?: string
	headerClass?: string
	sortJustify?: 'start' | 'end'
	sortColumnName?: keyof T | string
	filterType?: 'string' | 'dropdown' | 'dateRange'
	filterOptions?: {
		columnName: keyof T | string
		options?: FilterDropdownOption[]
	}
	width?: string
}

interface ListingProps<T> {
	name: string
	namePlural: string
	getIDFunc: (type: T) => string // (t: T) => t.myID
	selectedActions?: ListingAction[]
	list: (T & { children?: (T & { child?: React.ReactNode; children?: T[] })[] })[]
	columns: ListingColumn<T>[]
	defaultSort: Sort.SortFilter<T>
	isLoading: boolean
	paginationControls: 'top' | 'bottom' | 'both' | 'none'
	customButtons?: ReactNode[]
	customFilters?: ReactNode[]
	externalSearchFilters?: SearchFilter<T>
	listingBodyOnly?: boolean
	sectionStyles?: SectionStyles
	borderRadius?: boolean
	boxShadow?: boolean
	smallPagination?: boolean
	autoExpand?: boolean
	initialExpanded?: Record<string, boolean>
	headerPadRow?: boolean
	hideDisplayCount?: boolean
	handleGetMore?: () => void
	handleHotRefresh?: () => Promise<void>
	responsive?: boolean
	pageFilter?: Paging.FiltersState // Allows parent to control pagination
	setPageFilter?: React.Dispatch<React.SetStateAction<Paging.FiltersState>>
}

type SearchFilter<T> = Record<keyof Partial<T> | string, string>

const Listing = <T,>(props: ListingProps<T>) => {
	const [pageFilter, setPageFilter] =
		props.pageFilter && props.setPageFilter
			? [props.pageFilter, props.setPageFilter]
			: React.useState<Paging.FiltersState>({ page: 1, pageSize: Paging.getPagingPreferences() })
	const [internalSearchFilters, setInternalSearchFilters] = React.useState<SearchFilter<T>>({} as SearchFilter<T>)
	const [sortFilters, setSortFilters] = React.useState<Sort.SortFilter<T>>(props.defaultSort)
	const [selectFilter, setSelectFilter] = React.useState<string[]>([])
	const [expandedChildren, setExpandedChildren] = React.useState<Record<string, boolean>>(props.initialExpanded || {})
	const [localLoading, setLocalLoading] = React.useState<boolean>(false)

	const searchFilters = props.externalSearchFilters || internalSearchFilters

	const handleHotRefresh = async () => {
		if (props.handleHotRefresh) {
			setLocalLoading(true)
			await props.handleHotRefresh()
			setLocalLoading(false)
		}
	}

	const setSelect = (id: string, checked: boolean) => {
		if (checked && id === 'All') {
			setSelectFilter(props.list.map(props.getIDFunc))
		} else if (checked) {
			setSelectFilter([...selectFilter, id])
		} else if (id === 'All') {
			setSelectFilter([])
		} else {
			setSelectFilter(selectFilter.filter((t) => t !== id))
		}
	}

	const searchFilterFunction = (record: T) =>
		props.columns.every((column) => {
			if (column.filterOptions) {
				if (column.filterType === 'dateRange') {
					const low = searchFilters[String(column.filterOptions.columnName) + '-low']
					const high = searchFilters[String(column.filterOptions.columnName) + '-high']
					const value = column.value(record)
					if (low === undefined && high === undefined) {
						return true
					} else if (low && high) {
						return value >= low && value <= high
					} else if (low) {
						return value >= low
					} else if (high) {
						return value <= high
					}
					return true
				} else {
					const filterText = searchFilters[column.filterOptions.columnName]
					const value = column.value(record)
					if (filterText === 'Any' && column.filterType === 'dropdown') {
						return true
					} else if (filterText && value && column.filterType === 'dropdown') {
						return value.toLowerCase() === filterText.toLowerCase()
					} else if (filterText && value) {
						return value.toLowerCase().includes(filterText.toLowerCase())
					} else if (filterText && !value) {
						return false
					}
				}
			}
			return true
		})
	const pageFilterFunction = (_record: T, index: number) => Paging.pageFilter(pageFilter, index)
	const sortFunction = Sort.getSortFunction(sortFilters, props.columns)

	const unFilteredRecords = props.list
	const filteredRecords = unFilteredRecords.filter(searchFilterFunction)
	const sortedFilteredRecords = filteredRecords.sort(sortFunction)
	const paginatedFilteredSortRecords = props.paginationControls === 'none' ? sortedFilteredRecords : sortedFilteredRecords.filter(pageFilterFunction)

	const hasSelectedActions = props.selectedActions && props.selectedActions.length > 0
	const hasChildren = props.list.some((row) => row.children)
	const colSpanFullWidth = props.columns.length + (hasSelectedActions ? 1 : 0) // don't add the row for has children because it gets indented

	const PageSizeDropdown = () => (
		<Dropdown className="small-pagination">
			<Dropdown.Toggle id="dropdown-action" variant="secondary">
				{pageFilter.pageSize} records per page
			</Dropdown.Toggle>
			<Dropdown.Menu>
				{[10, 25, 50, 100].map((pageSize) => (
					<Dropdown.Item
						key={pageSize}
						onClick={() => {
							setPageFilter({ page: 1, pageSize })
							Paging.storePagingPreferences(pageSize)
						}}
					>
						{pageSize} records per page
					</Dropdown.Item>
				))}
			</Dropdown.Menu>
		</Dropdown>
	)

	const PaginationControls = () => (
		<>
			<Col sm="auto">
				<PageSizeDropdown />
			</Col>
			<Col sm="auto">
				{props.smallPagination ? (
					<Paging.PaginationSmall
						filters={pageFilter}
						setFilterDispatch={setPageFilter}
						filterDispatch={pageFilter}
						allPaginatedRecordsLength={filteredRecords.length}
						onLoadMoreClick={props.handleGetMore}
					/>
				) : (
					<Paging.Pagination
						filters={pageFilter}
						setFilterDispatch={setPageFilter}
						filterDispatch={pageFilter}
						allPaginatedRecordsLength={filteredRecords.length}
						onLoadMoreClick={props.handleGetMore}
					/>
				)}
			</Col>
		</>
	)

	const ExpandedControl = (props: { itemId: string; hasChild: boolean; depth: number }) => {
		const toggleExpanded = () => {
			setExpandedChildren({ ...expandedChildren, [props.itemId]: !expandedChildren[props.itemId] })
		}

		if (!hasChildren) {
			return null
		}

		return (
			<>
				{props.depth > 0 && <td colSpan={props.depth}></td>}
				<td>
					<div>
						{props.hasChild ? (
							expandedChildren[props.itemId] ? (
								<ChevronDown size={20} onClick={toggleExpanded} />
							) : (
								<ChevronRight size={20} onClick={toggleExpanded} />
							)
						) : (
							<Minus size={24} color={'#fff'} />
						)}
					</div>
				</td>
			</>
		)
	}

	const ExpandAllControl = (props: {
		items: (T & { children?: (T & { child?: React.ReactNode; children?: T[] })[] })[]
		itemIdFunc: (type: T) => string
	}) => {
		const open = Object.values(expandedChildren).some((e) => e == true) // Open if any rows are expanded

		const toggleExpanded = (set: boolean) => {
			// Shortcut to remove all open when minimising all
			if (!set) {
				setExpandedChildren({})
				return
			}

			// Get all the children ids and set as true
			const ids: Record<string, boolean> = {}
			props.items.forEach((i) => {
				ids[props.itemIdFunc(i)] = set
				if (i.children) {
					i.children.forEach((c) => {
						ids[props.itemIdFunc(c)] = set
						if (c.children) {
							c.children.forEach((cc) => {
								ids[props.itemIdFunc(cc)] = set
							})
						}
					})
				}
			})
			setExpandedChildren(ids)
		}

		return (
			<OverlayTrigger placement="right" overlay={<Tooltip id="expand-toggle-tooltip">{open ? 'Minimise All' : 'Expand All'}</Tooltip>}>
				{open ? <ChevronDown size={20} onClick={() => toggleExpanded(false)} /> : <ChevronRight size={20} onClick={() => toggleExpanded(true)} />}
			</OverlayTrigger>
		)
	}

	const ListingTable = React.useMemo(
		() => (
			<Table
				borderless
				responsive={props.responsive}
				className={`mb-0 table-listing ${props.boxShadow ? 'box-shadow' : ''} ${props.borderRadius ? 'border-radius' : ''}`}
			>
				<thead style={props.sectionStyles?.thead}>
					<tr>
						{hasChildren && !props.autoExpand && (
							<th style={{ width: '45px' }}>
								<ExpandAllControl items={props.list} itemIdFunc={props.getIDFunc} />
							</th>
						)}
						{hasSelectedActions && (
							<th>
								<Row>
									<Col className="center-flex">
										<Form.Control
											style={{ boxShadow: 'none', width: '16px', height: '16px', appearance: 'revert' }}
											onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
												setSelect(event.target.value, event.target.checked)
											}}
											type="checkbox"
											id={'select_' + props.name}
											name={'select_' + props.name}
											value={'All'}
										/>
									</Col>
								</Row>
							</th>
						)}
						{props.columns.map((column, index) => (
							<th key={index} style={{ width: column.width || 'inherit' }} className={column.headerClass}>
								{column.showHeader && column.headerText && column.sortColumnName && (
									<>
										<Row>
											<Col className="upper-table-header">
												<Sort.SortButton
													sort={sortFilters}
													setSort={setSortFilters}
													columnName={column.sortColumnName}
													preIconContent={<span>{column.headerText}</span>}
													sortJustify={column.sortJustify}
												/>
												{column.filterType && column.filterOptions && column.filterType === 'dateRange' && (
													<div style={{ display: 'flex', justifyContent: 'space-around' }}>
														<div>Start</div>
														<div>End</div>
													</div>
												)}
											</Col>
										</Row>
										<Row>
											<Col>
												{column.filterType && column.filterOptions && column.filterType === 'string' && (
													<Form.Control
														onChange={(event) => {
															setInternalSearchFilters({
																...searchFilters,
																[column.filterOptions!.columnName]: event.target.value,
															})
														}}
														type="text"
														id={`search_${String(column.filterOptions.columnName)}`}
														name={`search_${String(column.filterOptions.columnName)}`}
														value={searchFilters[column.filterOptions.columnName] || ''}
														placeholder="Type to filter"
													/>
												)}
												{column.filterType && column.filterOptions && column.filterType === 'dropdown' && (
													<Dropdown>
														<Dropdown.Toggle className="form" variant="secondary" id="dropdown-assetType">
															{column.filterOptions?.options?.find(
																(option) => option.value === searchFilters[column.filterOptions!.columnName]
															)?.text || 'Any'}
														</Dropdown.Toggle>
														<Dropdown.Menu>
															<Dropdown.Item
																onClick={() =>
																	setInternalSearchFilters({
																		...searchFilters,
																		[column.filterOptions!.columnName]: 'Any',
																	})
																}
															>
																Any
															</Dropdown.Item>
															{column.filterOptions.options &&
																column.filterOptions.options.map((option) => (
																	<Dropdown.Item
																		key={option.value}
																		onClick={() =>
																			setInternalSearchFilters({
																				...searchFilters,
																				[column.filterOptions!.columnName]: option.value,
																			})
																		}
																	>
																		{option.text}
																	</Dropdown.Item>
																))}
														</Dropdown.Menu>
													</Dropdown>
												)}
												{column.filterType && column.filterOptions && column.filterType === 'dateRange' && (
													<>
														<Form.Control
															onChange={(event) => {
																setInternalSearchFilters({
																	...searchFilters,
																	[String(column.filterOptions!.columnName) + '-low']: event.target.value,
																})
															}}
															type="date"
															max={searchFilters[String(column.filterOptions.columnName) + '-high'] || ''}
															id={`search_${String(column.filterOptions.columnName)}`}
															name={`search_${String(column.filterOptions.columnName)}`}
															value={searchFilters[String(column.filterOptions.columnName) + '-low'] || ''}
															style={{ width: '50%', display: 'inline-block ' }}
														/>
														<Form.Control
															onChange={(event) => {
																setInternalSearchFilters({
																	...searchFilters,
																	[String(column.filterOptions!.columnName) + '-high']: event.target.value,
																})
															}}
															type="date"
															min={searchFilters[String(column.filterOptions.columnName) + '-low'] || ''}
															id={`search_${String(column.filterOptions.columnName)}`}
															name={`search_${String(column.filterOptions.columnName)}`}
															value={searchFilters[String(column.filterOptions.columnName) + '-high'] || ''}
															style={{ width: '50%', display: 'inline-block ' }}
														/>
													</>
												)}
											</Col>
										</Row>
									</>
								)}
							</th>
						))}
					</tr>
				</thead>

				<tbody>
					{props.isLoading && (
						<tr>
							<td colSpan={colSpanFullWidth}>
								<div className="center-flex p-5">
									<Loading show />
								</div>
							</td>
						</tr>
					)}
					{!props.isLoading && (
						<>
							{props.headerPadRow && (
								<tr className="header-pad-row">
									<td colSpan={colSpanFullWidth}></td>
								</tr>
							)}
							{paginatedFilteredSortRecords.map((item) => (
								<React.Fragment key={props.getIDFunc(item)}>
									<tr
										key={props.getIDFunc(item)}
										className={`grouped-row ${expandedChildren[props.getIDFunc(item)] || props.autoExpand ? 'expanded-listing' : ''}`}
										style={props.sectionStyles?.trDepth0}
									>
										{!props.autoExpand && <ExpandedControl hasChild={!!item.children} itemId={props.getIDFunc(item)} depth={0} />}
										{itemRender(item, { hasSelectedActions, columns: props.columns, getIDFunc: props.getIDFunc, selectFilter, setSelect })}
									</tr>
									{item.children &&
										(expandedChildren[props.getIDFunc(item)] === true || props.autoExpand) &&
										item.children.sort(sortFunction).map((childItem) => (
											<React.Fragment key={`${props.getIDFunc(item)}-${props.getIDFunc(childItem)}`}>
												<tr
													key={`${props.getIDFunc(item)}-${props.getIDFunc(childItem)}`}
													className="child-listing"
													style={props.sectionStyles?.trDepth1}
												>
													{!props.autoExpand && (
														<ExpandedControl
															hasChild={!!childItem.child || !!childItem.children}
															itemId={props.getIDFunc(childItem)}
															depth={1}
														/>
													)}
													{itemRender(childItem, {
														hasSelectedActions,
														columns: props.columns,
														getIDFunc: props.getIDFunc,
														selectFilter,
														setSelect,
													})}
												</tr>
												{childItem.child && (expandedChildren[props.getIDFunc(childItem)] === true || props.autoExpand) && (
													<tr style={props.sectionStyles?.trDepth2}>
														<td colSpan={1}></td>
														<td colSpan={colSpanFullWidth} className="p-0">
															{childItem.child}
														</td>
													</tr>
												)}
												{childItem.children &&
													(expandedChildren[props.getIDFunc(childItem)] === true || props.autoExpand) &&
													childItem.children.sort(sortFunction).map((grandChildItem) => (
														<tr
															key={`${props.getIDFunc(item)}-${props.getIDFunc(childItem)}-${props.getIDFunc(grandChildItem)}`}
															className="grandchild-listing"
															style={props.sectionStyles?.trDepth2}
														>
															{!props.autoExpand && <td></td>}
															{itemRender(grandChildItem, {
																hasSelectedActions,
																columns: props.columns,
																getIDFunc: props.getIDFunc,
																selectFilter,
																setSelect,
															})}
														</tr>
													))}
											</React.Fragment>
										))}
								</React.Fragment>
							))}
							{paginatedFilteredSortRecords.length === 0 && (
								<tr>
									<td colSpan={colSpanFullWidth}>
										<div className="center-flex p-5">No {props.namePlural} found</div>
									</td>
								</tr>
							)}
						</>
					)}
				</tbody>
			</Table>
		),
		[
			props.boxShadow,
			props.borderRadius,
			props.sectionStyles,
			hasChildren,
			props.autoExpand,
			hasSelectedActions,
			props.name,
			props.columns,
			setInternalSearchFilters,
			searchFilters,
			props.isLoading,
			props.headerPadRow,
			paginatedFilteredSortRecords,
			props.getIDFunc,
			expandedChildren,
		]
	)

	if (props.listingBodyOnly) {
		return ListingTable
	}

	return (
		<>
			<Row className="align-items-center py-3">
				{props.customButtons?.map((button, index) => (
					<Col xs="auto" key={index}>
						{button}
					</Col>
				))}
				{hasSelectedActions && (
					<Col sm={'auto'}>
						<Dropdown>
							<Dropdown.Toggle style={{ display: 'flex', alignItems: 'center' }} id="dropdown-action">
								Bulk Actions
							</Dropdown.Toggle>
							<Dropdown.Menu>
								{props.selectedActions!.map((action, i) => (
									<Dropdown.Item
										key={i}
										onClick={async () => {
											await action.method(selectFilter)
											setSelectFilter([])
										}}
									>
										{action.name}
									</Dropdown.Item>
								))}
							</Dropdown.Menu>
						</Dropdown>
					</Col>
				)}
				{hasSelectedActions && (
					<Col sm={'auto'}>
						<span className="span-grey">
							{selectFilter.length /* || paginatedFilteredSortRecords.length */} {props.list.length === 1 ? props.name : props.namePlural}{' '}
							selected
						</span>
					</Col>
				)}
				<Col />
				{props.handleHotRefresh && (
					<Col sm={'auto'}>
						<RefreshCw key="refresh" onClick={handleHotRefresh} style={{ animation: localLoading ? 'spin-anim 3s linear infinite' : undefined }} />
					</Col>
				)}
				{(props.paginationControls === 'top' || props.paginationControls === 'both') && <PaginationControls />}
			</Row>

			{props.customFilters && props.customFilters.length && (
				<Row className="align-items-center py-3">
					{props.customFilters.map((filter, index) => (
						<Col xs="auto" key={index}>
							{filter}
						</Col>
					))}
				</Row>
			)}

			<Row>
				<Col className="table-wrapper">
					{ListingTable}
					<Row className="py-3 align-items-center">
						<Col className="d-flex">
							{!props.hideDisplayCount && (
								<span className="span-body-2">
									{unFilteredRecords.length ? (
										<>
											Displaying <span className="span-blue">{Paging.calculatePageStart(pageFilter)}</span> to{' '}
											<span className="span-blue">{Paging.calculatePageTo(pageFilter, unFilteredRecords.length)}</span> of{' '}
											{unFilteredRecords.length}
										</>
									) : (
										<>No {props.namePlural} found</>
									)}
								</span>
							)}
						</Col>
						{(props.paginationControls === 'bottom' || props.paginationControls === 'both') && <PaginationControls />}
					</Row>
				</Col>
			</Row>
		</>
	)
}

export { Listing }
export type { FilterDropdownOption, ListingAction, ListingColumn, SearchFilter, SectionStyles }
